import { ActionIcon, Badge, Box, Button, Card, Group, Loader, Modal, Text, Tooltip, Transition } from '@mantine/core';
import { IconDownload, IconPrinter, IconX } from '@tabler/icons-react';
import React, { lazy, Suspense, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFileViewerStore } from '@mygpt/app/chat/stores/useFileViewerStore';

import type { EngineApi, EngineProps } from './engines/types';
import classes from './fileViewer.module.css';
import { printPayload } from './print';
import { FileViewerKind, mimeOf } from './registry';
import { useFileBytes } from './useFileBytes';

/**
 * 回答正文文件卡片（FileChip）的预览面板：浏览器本地解析原文件，纯查看。
 *
 * 和角标的 PDFViewer 并列存在、形态一致（聊天页右侧分栏 / 窄屏 Modal / 独立页整页），
 * 但走的是另一条链路：不经后端 to_pdf，不请求高亮；按格式分流到各引擎（engines/），
 * 引擎按需 lazy 加载，不进主包。工具条只有下载（原字节）和打印（浏览器 window.print）。
 */

// 弹窗形态与 PDFViewer 同层；工具条 Tooltip 走 portal，默认层级（300）会被弹窗（500）盖住，要压在它上面
const MODAL_Z_INDEX = 500;

const ENGINES: Record<FileViewerKind, React.LazyExoticComponent<React.ComponentType<EngineProps>>> = {
  pdf: lazy(() => import('./engines/PdfDoc')),
  docx: lazy(() => import('./engines/DocxDoc')),
  pptx: lazy(() => import('./engines/PptxDoc')),
  sheet: lazy(() => import('./engines/SheetDoc')),
  text: lazy(() => import('./engines/TextDoc')),
  image: lazy(() => import('./engines/ImageDoc')),
};

/** 引擎渲染崩溃只影响查看区，不拖垮聊天页 */
class EngineBoundary extends React.Component<
  { fallback: React.ReactNode; children: React.ReactNode },
  { failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch(error: unknown) {
    console.error('文件预览：引擎渲染出错', error);
  }

  override render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

const saveBlob = (data: ArrayBuffer, name: string, ext: string) => {
  const url = URL.createObjectURL(new Blob([data], { type: mimeOf(ext) }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};

interface FileViewerProps {
  // 是否启用嵌入式 UI（聊天页右侧分栏），默认是弹窗式 UI
  embedded?: boolean;
  // 弹窗宽度，默认维持现有页面的 70%
  modalSize?: string | number;
  // 关闭行为，默认清空状态收起面板；独立预览页覆盖为关闭标签页
  onClose?: () => void;
  // 独立整页展示：去掉侧栏形态的左侧留白与左边框
  standalone?: boolean;
}

export const FileViewer: React.FC<FileViewerProps> = ({
  embedded = false,
  modalSize = '70%',
  onClose,
  standalone = false,
}) => {
  const { t } = useTranslation();
  const { file_id, fileName: givenName, page, close } = useFileViewerStore();
  const bytes = useFileBytes(file_id, givenName);
  const apiRef = useRef<EngineApi | null>(null);
  const [printing, setPrinting] = useState(false);
  const handleClose = onClose ?? close;

  const fileName =
    (bytes.status === 'ready' || bytes.status === 'unsupported' ? bytes.fileName : givenName) ||
    t('preview_panel.title');
  const ext = bytes.status === 'ready' || bytes.status === 'unsupported' ? bytes.ext : '';
  const canDownload = bytes.status === 'ready' || bytes.status === 'unsupported';

  const download = () => {
    if (bytes.status === 'ready' || bytes.status === 'unsupported') saveBlob(bytes.data, fileName, bytes.ext);
  };

  const print = async () => {
    if (!apiRef.current || printing) return;
    setPrinting(true);
    try {
      await printPayload(await apiRef.current.getPrint(), fileName);
    } catch (error) {
      console.error('文件预览：打印失败', error);
    } finally {
      setPrinting(false);
    }
  };

  const renderBody = () => {
    if (bytes.status === 'loading') {
      return (
        <div className={classes.empty}>
          <Loader color="blue" size="sm" />
          <Text size="sm" fw={500}>
            {t('file_viewer.loading')}
          </Text>
        </div>
      );
    }
    if (bytes.status === 'error') return <div className={classes.empty}>{t('file_viewer.load_error')}</div>;
    if (bytes.status === 'unsupported') {
      return (
        <div className={classes.empty}>
          <Text size="sm">{t('file_viewer.unsupported')}</Text>
          <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} onClick={download}>
            {t('file_viewer.download')}
          </Button>
        </div>
      );
    }
    const Engine = ENGINES[bytes.kind];
    return (
      <EngineBoundary
        key={`${file_id}:${bytes.kind}`}
        fallback={<div className={classes.empty}>{t('file_viewer.parse_error')}</div>}
      >
        <Suspense fallback={<div className={classes.empty}>{t('file_viewer.loading')}</div>}>
          <Engine data={bytes.data} ext={bytes.ext} fileName={bytes.fileName} page={page} apiRef={apiRef} />
        </Suspense>
      </EngineBoundary>
    );
  };

  const header = (
    <Group justify="space-between" wrap="nowrap" gap="xs">
      <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
        <Text fw={500} truncate title={fileName}>
          {fileName}
        </Text>
        {ext && (
          <Badge size="xs" variant="light" radius="sm">
            {ext.toUpperCase()}
          </Badge>
        )}
      </Group>
      <Group gap={2} wrap="nowrap">
        <Tooltip zIndex={MODAL_Z_INDEX + 1} label={t('file_viewer.download')}>
          <ActionIcon variant="subtle" color="gray" disabled={!canDownload} onClick={download}>
            <IconDownload size={18} />
          </ActionIcon>
        </Tooltip>
        <Tooltip zIndex={MODAL_Z_INDEX + 1} label={t('file_viewer.print')}>
          <ActionIcon
            variant="subtle"
            color="gray"
            disabled={bytes.status !== 'ready'}
            loading={printing}
            onClick={print}
          >
            <IconPrinter size={18} />
          </ActionIcon>
        </Tooltip>
        <Tooltip zIndex={MODAL_Z_INDEX + 1} label={t('preview_panel.close')}>
          <ActionIcon variant="subtle" color="gray" onClick={handleClose}>
            <IconX size={18} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Group>
  );

  // 没有打开任何文件时，分栏形态不占位（弹窗形态靠 opened 控制）
  if (embedded && !file_id) return null;

  if (embedded) {
    return (
      <div
        className="transition-all bg-[#fafbff] h-full"
        style={{ position: 'relative', flex: '1', overflow: 'hidden' }}
      >
        <Transition
          mounted
          transition="fade-left"
          duration={150}
          timingFunction="ease-out"
          enterDelay={100}
          keepMounted
        >
          {(styles) => (
            <div className={`h-full flex-1 ${standalone ? '' : 'ml-2'}`} style={styles}>
              <Card className={`${standalone ? '' : 'border-l'} flex flex-col`} h="100%" style={{ borderRadius: 0 }}>
                <Card.Section inheritPadding py="xs">
                  {header}
                </Card.Section>
                <Card.Section className={classes.body}>{renderBody()}</Card.Section>
              </Card>
            </div>
          )}
        </Transition>
      </div>
    );
  }

  return (
    <Modal
      opened={!!file_id}
      onClose={handleClose}
      centered
      transitionProps={{ transition: 'fade', duration: 600, timingFunction: 'ease' }}
      size={modalSize}
      padding={0}
      withCloseButton={false}
      zIndex={MODAL_Z_INDEX}
    >
      <Box className="flex flex-col h-[80dvh]">
        <Box px="md" py="xs" className="border-b">
          {header}
        </Box>
        <Box className={classes.body}>{renderBody()}</Box>
      </Box>
    </Modal>
  );
};
